import React, { FunctionComponent, useState } from 'react';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { useFormContext } from 'react-hook-form';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import dateFnsFormat from 'date-fns/format';
import 'react-day-picker/lib/style.css';
import { DATE_FORMAT, parseDate } from 'helpers/date';
import { ClassProp } from 'helpers/types';

import FormErrors from '../FormError';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  name: string;
  label: string;
  placeholder?: Date;
  size?: string;
  tabIndex?: number;
  id?: string;
  onDateChange?: () => void;
  value?: Date;
  disabled?: boolean;
};

const styles = {
  container: {
    width: '100%',
    marginTop: '.25rem !important',
    justifyContent: 'center !important',
  },
  small: {
    width: '31%',
    marginRight: '.55rem',
  },
  medium: {
    width: '48%',
    marginRight: '.55rem',
  },
  large: {
    width: '100%',
  },
  fieldContainer: {},
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  fieldInput: {
    display: 'flex',
    flex: '1 0 auto',
    maxWidth: '100%',
    width: '100%',
    textAlign: 'left',
    margin: '0',
    outline: '0',
    tapHighlightColor: 'rgba(255,255,255,0)',
    lineHeight: '1.21em',
    padding: '.67857143rem 1rem',
    fontSize: '1em',
    background: '#fff',
    border: '.0625rem solid rgba(34,36,38,.15)',
    color: 'rgba(0,0,0,.87)',
    borderRadius: '.28571429rem',
    boxShadow: '0 0 0 0 transparent inset',
    transition: 'color .1s ease,border-color .1s ease',
  },
  fieldError: {},
  datePickerWrapper: {
    '& .DayPickerInput': {
      width: '100% !important',
    },
    '& .DayPickerInput input': {
      width: '100% !important',
      margin: 0,
      maxWidth: '100%',
      outline: 0,
      textAlign: 'left',
      lineHeight: '1.21428571em',
      fontFamily: 'DOSH !important',
      padding: '.67857143em 1em',
      background: '#fff',
      border: '0.0625rem solid rgba(34,36,38,.15)',
      color: 'rgba(0,0,0,.87)',
      borderRadius: '.28571429rem',
    },
  },
};

const formatDate = (date: Date, format: string) => dateFnsFormat(date, format);

const FormDateInput: FunctionComponent<Props> = ({
  classes,
  label,
  name,
  placeholder,
  size,
  onDateChange,
  disabled,
  ...rest
}) => {
  const { errors, setValue, trigger } = useFormContext();

  const [selectedDay, setSelectedDay] = useState(placeholder);

  const handleDateChange = (
    selectedDay: Date,
    __: any,
    dayPickerInput: DayPickerInput,
  ) => {
    const input = dayPickerInput.getInput();
    const { name } = input;
    setValue(name, selectedDay);
    setSelectedDay(selectedDay);
    trigger(name);
    if (onDateChange) {
      onDateChange();
    }
  };

  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
      })}
    >
      <label htmlFor={name}>
        <span className={classes.fieldLabel}>{label}</span>
        <div className={classes.datePickerWrapper}>
          <DayPickerInput
            onDayChange={handleDateChange}
            formatDate={formatDate}
            format={DATE_FORMAT}
            dayPickerProps={{
              className: 'selectable',
              selectedDays: selectedDay,
              initialMonth: selectedDay,
            }}
            parseDate={parseDate}
            placeholder={
              placeholder ? `${dateFnsFormat(placeholder, DATE_FORMAT)}` : ''
            }
            inputProps={{
              name,
              disabled,
            }}
            {...rest}
          />
        </div>
      </label>
      {errors && <FormErrors name={name} errors={errors} />}
    </div>
  );
};

export default withStyles(styles)(FormDateInput);
